import { Dispatch, SetStateAction, useState } from 'react';

import { Auth } from 'aws-amplify';
import { useFormik } from 'formik';
import { Button, ErrorMessage, UiInput, LinkButton } from 'ui-kit';
import * as yup from 'yup';

import { YUP_PASSWORD_MATCH_MESSAGE, YUP_PASSWORD_MATCH_REGEX } from './validation';

type StartResetFormProps = {
  setEmail: Dispatch<SetStateAction<string>>;
  setIsResettingPassword: Dispatch<SetStateAction<boolean>>;
};

const StartResetForm = ({ setEmail, setIsResettingPassword }: StartResetFormProps) => {
  const [error, setError] = useState<string>();
  const formik = useFormik({
    initialValues: { email: '' },
    validationSchema: yup.object().shape({
      email: yup.string().required('Email is required').email('Invalid email address'),
    }),
    onSubmit: async (values) => {
      try {
        await Auth.forgotPassword(values.email);
        setEmail(values.email);
        setIsResettingPassword(true);
      } catch (err: any) {
        setError(err.message);
      }
    },
  });

  return (
    <form onSubmit={formik.handleSubmit} className="flex flex-col gap-3">
      <div>
        <UiInput
          name="email"
          type="text"
          size="extra-large"
          variant="dark"
          label="Email"
          placeholder="Institution Email Address"
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.email && Boolean(formik.errors.email)}
        />
        <ErrorMessage className="mt-1 font-heebo">
          {formik.touched.email && formik.errors.email}
        </ErrorMessage>
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </div>

      <Button
        label="Next"
        buttonType="submit"
        type="primary_light"
        disabled={!formik.isValid}
        className="bg-gradient-to-r from-violet-500 to-violet-600"
      >
        Next
      </Button>
    </form>
  );
};

type SubmitResetFormProps = {
  email: string;
  setIsResetSuccess: Dispatch<SetStateAction<boolean>>;
};

const SubmitResetForm = ({ email, setIsResetSuccess }: SubmitResetFormProps) => {
  const [error, setError] = useState<string>();
  const formik = useFormik({
    initialValues: {
      email,
      code: '',
      password: '',
      confirm_password: '',
    },
    validationSchema: yup.object().shape({
      code: yup.string().required('Code is required'),
      password: yup
        .string()
        .required()
        .matches(YUP_PASSWORD_MATCH_REGEX, YUP_PASSWORD_MATCH_MESSAGE),
      confirm_password: yup
        .string()
        .oneOf([yup.ref('password'), null], "Passwords don't match")
        .required('Confirm Password is required'),
    }),
    onSubmit: async (values) => {
      try {
        await Auth.forgotPasswordSubmit(values.email, values.code, values.password);
        setIsResetSuccess(true);
      } catch (err: any) {
        setError(err.message);
      }
    },
  });

  return (
    <form onSubmit={formik.handleSubmit} className="mt-3 flex flex-col gap-3">
      <div>
        <UiInput
          name="code"
          label="Verification Code"
          placeholder="Email Verification Code"
          value={formik.values.code}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          size="extra-large"
          variant="dark"
          error={formik.touched.code && Boolean(formik.errors.code)}
        />
        <ErrorMessage className="mt-1 font-heebo">
          {formik.touched.code && formik.errors.code}
        </ErrorMessage>
      </div>

      <div>
        <UiInput
          name="password"
          type="password"
          label="New Password"
          placeholder="New Password"
          value={formik.values.password}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          size="extra-large"
          variant="dark"
          error={formik.touched.password && Boolean(formik.errors.password)}
        />
        <ErrorMessage className="mt-1 font-heebo">
          {formik.touched.password && formik.errors.password}
        </ErrorMessage>
      </div>

      <div>
        <UiInput
          name="confirm_password"
          type="password"
          label="Confirm Password"
          placeholder="Confirm Password"
          value={formik.values.confirm_password}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          size="extra-large"
          variant="dark"
          error={formik.touched.confirm_password && Boolean(formik.errors.confirm_password)}
        />
        <ErrorMessage className="mt-1 font-heebo">
          {formik.touched.confirm_password && formik.errors.confirm_password}
        </ErrorMessage>

        {error && <ErrorMessage className="mt-1 font-heebo">{error}</ErrorMessage>}
      </div>

      <Button
        label="Submit"
        buttonType="submit"
        type="primary_light"
        disabled={!formik.isValid}
        className="bg-gradient-to-r from-violet-500 to-violet-600"
      >
        Submit
      </Button>
    </form>
  );
};

type Props = {
  onFlip: () => void;
};

const ForgotPassword = ({ onFlip }: Props): JSX.Element => {
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [isResetSuccess, setIsResetSuccess] = useState(false);
  const [email, setEmail] = useState('');

  return (
    <>
      <div className="mb-5 text-left font-heebo text-white">
        <h2 className="text-[24px] font-bold">Forgot Password</h2>
        <p className="mt-2 text-[16px]">Reset your password using your email address</p>
      </div>

      {isResetSuccess ? (
        <p className="text-green-400">Your password has been successfully reset.</p>
      ) : isResettingPassword ? (
        <SubmitResetForm email={email} setIsResetSuccess={setIsResetSuccess} />
      ) : (
        <StartResetForm setEmail={setEmail} setIsResettingPassword={setIsResettingPassword} />
      )}

      <div className="mt-2 text-left">
        <LinkButton
          text="Back to Sign In"
          onClick={onFlip}
          className="font-heebo text-sm"
          underline={false}
        />
      </div>
    </>
  );
};

export default ForgotPassword;
